// Platform drag-to-reorder primitive (KLUB-264). Port of the admin
// `components/ui/Sortable.tsx`: consumers pass `items` (IDs in current order)
// and `onReorder(nextIds)` and never touch a drag event. Pointer distance 8
// separates a click from a drag; the keyboard sensor plus pt-BR announcements
// make it operable without a mouse. Restyled with the platform light tokens.

import { useCallback, useMemo, type CSSProperties, type HTMLAttributes, type ReactNode } from "react";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { GripIcon } from "./icons";
import { colors, radius, shadow } from "../lib/tokens";
import { SCREEN_READER_INSTRUCTIONS, buildAnnouncements, computeReorder } from "./Sortable.utils";

interface SortableProviderProps {
  items: string[];
  onReorder: (nextIds: string[]) => void;
  /** Names an item for the screen-reader announcements. */
  labelOf?: (id: string) => string | undefined;
  /** When true no drag can start (and a drop is ignored). */
  disabled?: boolean;
  children: ReactNode;
}

export function SortableProvider({ items, onReorder, labelOf, disabled = false, children }: SortableProviderProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (disabled) return;
      const { active, over } = event;
      if (!over) return;
      const next = computeReorder(items, String(active.id), String(over.id));
      if (next) onReorder(next);
    },
    [items, onReorder, disabled],
  );

  const announcements = useMemo(() => buildAnnouncements(items, labelOf), [items, labelOf]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
      onDragEnd={handleDragEnd}
      accessibility={{ announcements, screenReaderInstructions: SCREEN_READER_INSTRUCTIONS }}
    >
      <SortableContext items={items} strategy={verticalListSortingStrategy} disabled={disabled}>
        {children}
      </SortableContext>
    </DndContext>
  );
}

type RowProps = Omit<HTMLAttributes<HTMLTableRowElement>, "children">;

interface SortableRowProps extends RowProps {
  id: string;
  /** aria-label of the grip, e.g. "Arraste para reordenar Viagem". */
  handleLabel: string;
  dragDisabled?: boolean;
  /** Renders the row's cells; `handle` is the grip button to place in one. */
  children: (handle: ReactNode) => ReactNode;
}

/** A sortable `<tr>`. The grip is the only drag activator, so clicks on the
 *  rest of the row (opening the drawer) keep working. */
export function SortableRow({ id, handleLabel, dragDisabled = false, children, style, ...rowProps }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id });
  const rowStyle: CSSProperties = {
    ...style,
    transform: CSS.Transform.toString(transform),
    transition,
    position: "relative",
    ...(isDragging ? { zIndex: 1, background: colors.white, boxShadow: shadow.md } : {}),
  };
  const handle = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      aria-label={handleLabel}
      {...attributes}
      {...listeners}
      disabled={dragDisabled}
      // A click on the grip must not open the row's drawer.
      onClick={(e) => e.stopPropagation()}
      style={{ ...handleStyle, cursor: dragDisabled ? "not-allowed" : "grab", opacity: dragDisabled ? 0.4 : 1 }}
    >
      <GripIcon />
    </button>
  );
  return (
    <tr ref={setNodeRef} style={rowStyle} {...rowProps}>
      {children(handle)}
    </tr>
  );
}

const handleStyle: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  height: 28,
  padding: 0,
  border: "none",
  borderRadius: radius.sm,
  background: "transparent",
  color: colors.stoneSoft,
  // Without this, the browser's touch-scroll steals the drag gesture on touch.
  touchAction: "none",
};
